import React, { createContext, useContext, useState, useCallback } from 'react';
import { httpGet } from '../lib/http';
import { useAuth } from './AuthContext';

const StudyPlansContext = createContext(null);

export function StudyPlansProvider({ children }) {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [tracksById, setTracksById] = useState({});
  const [rawResponse, setRawResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadTracks = useCallback(async (userId) => {
    const id = userId || user?.id || user?.userId || user?._id;
    if (!id) return [];
    try {
      setLoading(true);
      const res = await httpGet(`/api/users/${encodeURIComponent(id)}/study-tracks`);
      let data = null;
      try { data = await res.json(); } catch (e) {}
      // store raw response for UI usage
      setRawResponse(data || null);
      const arr = (data && (data.tracks || data.data || data)) || [];
      const final = Array.isArray(arr) ? arr : [];
      const normalizedTracks = final.map(normalizeTrack);
      setTracks(normalizedTracks);
      const map = {};
      normalizedTracks.forEach(t => { const key = (t.id || t.pathId); if (key) map[key] = t; });
      setTracksById(map);
      return normalizedTracks;
    } catch (e) {
      setTracks([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  const upsertTrack = useCallback((track) => {
    if (!track) return;
    const key = track.id || track.pathId;
    if (!key) return;
    const normalized = normalizeTrack(track);
    setTracks(prev => {
      const exists = prev.find(t => (t.id || t.pathId) === key);
      if (exists) return prev.map(t => ((t.id || t.pathId) === key ? normalized : t));
      return [normalized, ...prev];
    });
    setTracksById(prev => ({ ...(prev || {}), [key]: normalized }));
    setRawResponse(prev => {
      if (!prev) return prev;
      // if prev is an object with `tracks` array, prepend
      if (Array.isArray(prev)) return [track, ...prev];
      if (prev && Array.isArray(prev.tracks)) return { ...prev, tracks: [track, ...prev.tracks] };
      return prev;
    });
  }, []);

  const clear = useCallback(() => setTracks([]), []);

  // Normalize API track payload to frontend-friendly shape
  function normalizeTrack(t) {
    if (!t) return t;
    const track = { ...t };
    const subjects = Array.isArray(track.subjects) ? track.subjects : [];
    const orderedSubjects = subjects.map((s, idx) => {
      const subj = { ...s };
      if (!subj.id) {
        const base = (subj.title || `subject-${idx}`);
        subj.id = `${(track.id || track.pathId || 'track')}-${idx}-${base.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      }
      const chapters = Array.isArray(subj.chapters) ? subj.chapters : [];
      const topics = [];
      chapters.forEach((ch, ci) => {
        const chTopics = Array.isArray(ch.topics) ? ch.topics : [];
        chTopics.forEach((tpc, ti) => {
          const title = (tpc && (tpc.title || tpc)) || `topic-${ci}-${ti}`;
          const topicId = `${subj.id}-topic-${ti}-${(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          topics.push({ id: topicId, title, estimatedMinutes: tpc.estimatedMinutes || 0, practiceCount: tpc.practiceCount || 0 });
        });
      });
      if (Array.isArray(subj.topics)) {
        subj.topics.forEach((tpc, i) => {
          const title = (tpc && (tpc.title || tpc)) || `topic-${i}`;
          const topicId = `${subj.id}-topic-extra-${i}-${(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          topics.push({ id: topicId, title, estimatedMinutes: tpc.estimatedMinutes || 0, practiceCount: tpc.practiceCount || 0 });
        });
      }
      subj.topics = topics;
      return subj;
    });
    track.orderedSubjects = orderedSubjects;
    return track;
  }

  const getTrack = useCallback((id) => {
    if (!id) return null;
    if (tracksById[id]) return tracksById[id];
    return tracks.find(t => (t.id || t.pathId) === id) || null;
  }, [tracks]);

  return (
    <StudyPlansContext.Provider value={{ tracks, loading, loadTracks, clear, getTrack, rawResponse, upsertTrack }}>
      {children}
    </StudyPlansContext.Provider>
  );
}

export function useStudyPlans() {
  const ctx = useContext(StudyPlansContext);
  if (!ctx) throw new Error('useStudyPlans must be used within StudyPlansProvider');
  return ctx;
}
